import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { filterCampusQuickLocations, toCampusPrediction } from '../campusQuickLocations';
import { getRecentLocations, addRecentLocation } from './recentLocations';

/** Campus quick-picks are hidden once this many recents exist (still deduped by name when mixed in). */
const CAMPUS_QUICK_PICK_RECENT_THRESHOLD = 4;

function campusPredictionsExcludingRecents(recentsRaw, query) {
  if (recentsRaw.length >= CAMPUS_QUICK_PICK_RECENT_THRESHOLD) {
    return [];
  }
  const recentNames = new Set(recentsRaw.map((r) => String(r.name).toLowerCase()));
  return filterCampusQuickLocations(query)
    .filter((loc) => !recentNames.has(loc.name.toLowerCase()))
    .map(toCampusPrediction);
}

const PlaceAutocomplete = forwardRef(({
  placeholder,
  onPlaceSelect,
  value,
  onChange,
  className = "",
  id = "",
  options = {},
  renderSuggestions,
  ...props
}, ref) => {
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const sessionToken = useRef(null);
  const debounceTimer = useRef(null);
  const containerRef = useRef(null);

  const [isLoaded, setIsLoaded] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsLoaded(true);
    } else {
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsLoaded(true);
          clearInterval(checkGoogleMaps);
        }
      }, 100);
      return () => clearInterval(checkGoogleMaps);
    }
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    if (!autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, [isLoaded]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showSuggestions) return undefined;
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPredictions([]);
        setShowSuggestions(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [showSuggestions]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    closeSuggestions: () => {
      setPredictions([]);
      setShowSuggestions(false);
    }
  }));

  const handleInputChange = (e) => {
    if (onChange) onChange(e);
    const val = e.target.value;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (!val || !autocompleteService.current) {
      setPredictions([]);
      setShowSuggestions(false);
      return;
    }

    debounceTimer.current = setTimeout(() => {
      const atlantaCenter = { lat: 33.7756, lng: -84.3963 };

      const request = {
        input: val,
        locationRestriction: {
          center: atlantaCenter,
          radius: 3000
        },
        componentRestrictions: { country: 'us' },
        sessionToken: sessionToken.current,
        ...options
      };

      autocompleteService.current.getPlacePredictions(request, (results, status) => {
        const recentsRaw = getRecentLocations();
        const googleOk =
          status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length;
        const googlePredictions = googleOk ? results : [];
        const campusMatches = campusPredictionsExcludingRecents(recentsRaw, val);
        const merged = [...googlePredictions, ...campusMatches];
        if (merged.length) {
          setPredictions(merged);
          setShowSuggestions(true);
        } else {
          setPredictions([]);
          setShowSuggestions(false);
        }
      });
    }, 300);
  };
  const handleInputFocus = () => {
    if (!value) {
      const recentsRaw = getRecentLocations();
      const recentPreds = recentsRaw.map((loc) => ({
        description: loc.name,
        structured_formatting: { main_text: loc.name, secondary_text: loc.address },
        recentLocation: true,
        ...loc
      }));
      const campusPreds = campusPredictionsExcludingRecents(recentsRaw, '');
      const next = [...recentPreds, ...campusPreds];
      setPredictions(next);
      setShowSuggestions(next.length > 0);
    }
  };

  const handlePredictionSelect = (prediction) => {
    if (prediction.campusQuickPick) {
      onPlaceSelect({
        name: prediction.name,
        address: prediction.address,
        coordinates: `${prediction.latitude},${prediction.longitude}`,
        placeId: prediction.placeId || '',
        types: [],
        fromAutocomplete: true
      });
      addRecentLocation({
        name: prediction.name,
        latitude: prediction.latitude,
        longitude: prediction.longitude,
        address: prediction.address
      });
      setPredictions([]);
      setShowSuggestions(false);
      return;
    }

    if (prediction.recentLocation) {
      // Directly use the data for recent locations
      onPlaceSelect({
        name: prediction.name,
        address: prediction.address,
        coordinates: `${prediction.latitude},${prediction.longitude}`,
        placeId: prediction.placeId || "",
        types: [],
        fromAutocomplete: false
      });
      setPredictions([]);
      setShowSuggestions(false);
      return;
    }

    // Otherwise, handle Google prediction as before
    const request = {
      placeId: prediction.place_id,
      fields: ['place_id', 'geometry', 'name', 'formatted_address', 'types'],
      sessionToken: sessionToken.current
    };
  
    placesService.current.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        const coords = place.geometry.location;
        const placeData = {
          name: place.name || place.formatted_address,
          address: place.formatted_address,
          coordinates: `${coords.lat()},${coords.lng()}`,
          placeId: place.place_id,
          types: place.types,
          fromAutocomplete: true
        };
  
        onPlaceSelect(placeData);
        addRecentLocation({
          name: place.name || place.formatted_address,
          latitude: coords.lat(),
          longitude: coords.lng(),
          address: place.formatted_address
        });
        setPredictions([]);
        setShowSuggestions(false);
  
        // Refresh session token
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    });
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        className={className}
        id={id}
        autoComplete="off"
        {...props}
      />
      {showSuggestions && renderSuggestions && renderSuggestions({
        predictions,
        onSelect: handlePredictionSelect
      })}
    </div>
  );
});

export default PlaceAutocomplete;
